import React, { useState } from 'react';
import { Printer, Loader2, Image as ImageIcon, FileText, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { downloadMasjidPosterPdf, PRESET_HADITHS } from '../utils/masjidPoster';
import { useZakatGlobal } from '../context/ZakatGlobalContext';
import { hp } from '../config/homepageStrings';

/**
 * One-click "Download masjid poster" button.
 *
 * Variants:
 *   - 'inline'  : compact outline button (used inside lists / cards)
 *   - 'primary' : full-bleed marketing CTA used on the homepage community strip.
 *                 Renders a proper stacked card (title + copy on top, action row
 *                 at the bottom) so the text column never wraps awkwardly next to
 *                 the download button — this used to happen when the card sat in
 *                 a narrow (~50%) grid column.
 *
 * Formats offered from the primary card:
 *   - A4 QR-poster (PDF, generated on-device with jsPDF)
 *   - Social share poster (PNG, direct-download from /public/share-poster.png)
 *   - A "Customise poster" link to /masjid-resources for hadith + language picker
 *
 * Pass an optional `masjidName` to stamp the bottom of the PDF poster with the
 * mosque / community-centre name.
 */

// The pre-rendered 1200x630 social poster shipped with the app.
const SHARE_POSTER_PATH = '/share-poster.png';

const triggerBrowserDownload = (href, filename) => {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const MasjidPosterButton = ({
  variant = 'inline',
  testid = 'masjid-poster',
  masjidName = '',
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const { selectedLanguage } = useZakatGlobal();
  const lang = selectedLanguage?.code || 'en';

  const downloadPdf = async () => {
    setBusy(true); setError(null);
    try {
      await downloadMasjidPosterPdf({
        masjidName,
        hadith: PRESET_HADITHS[0],
        richMode: true,
      });
    } catch (e) {
      setError(e.message || 'Could not generate the poster.');
    } finally {
      setBusy(false);
    }
  };

  const downloadPng = () => {
    triggerBrowserDownload(SHARE_POSTER_PATH, 'zakat-international-share-poster.png');
  };

  if (variant === 'primary') {
    return (
      <div
        data-testid={`${testid}-card`}
        className="relative rounded-2xl border border-stone-200 bg-white p-6 sm:p-7 shadow-[0_4px_24px_-16px_rgba(6,78,59,0.15)] flex flex-col h-full"
      >
        {/* Eyebrow */}
        <div className="inline-flex self-start items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-[#4A5568] bg-[#F3EFEA] border border-[#CBD2D9] px-2.5 py-1 rounded-full mb-3">
          <Printer className="w-3 h-3" /> {hp(lang, 'masjidBadge')}
        </div>

        {/* Title + description — full-width column, no side-by-side */}
        <h3 className="font-heading text-xl sm:text-2xl leading-tight text-[#0D1520] mb-2 tracking-tight">
          {hp(lang, 'masjidTitle')}
        </h3>
        <p className="text-sm text-stone-600 leading-relaxed mb-5 flex-1">
          1-page A4 poster with a large QR code. Print it, pin it to the notice
          board — help your community calculate their Zakat, plan inheritance
          and write an Islamic will. No accounts, no tracking.
        </p>

        {/* Action row — 2 primary format buttons + a customise link */}
        <div className="mt-auto space-y-2.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <Button
              data-testid={`${testid}-download`}
              onClick={downloadPdf}
              disabled={busy}
              className="bg-[#EDF5F1] hover:bg-[#EDF5F1] text-[#0D1520] h-11 rounded-xl justify-center"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              {busy ? 'Building PDF…' : hp(lang, 'masjidA4')}
            </Button>

            <Button
              data-testid={`${testid}-download-png`}
              onClick={downloadPng}
              variant="outline"
              className="border-[#E2DCD5] text-[#0D1520] hover:bg-[#F3EFEA] h-11 rounded-xl justify-center"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              {hp(lang, 'masjidSocial')}
            </Button>
          </div>

          <Link
            to="/masjid-resources"
            data-testid={`${testid}-customise`}
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-[#4A5568] hover:text-gold-600 transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Customise (12 hadiths · 15 languages · masjid name)
          </Link>
        </div>

        {error && (
          <p data-testid={`${testid}-error`} className="mt-2 text-xs text-red-700">{error}</p>
        )}
      </div>
    );
  }

  return (
    <Button
      data-testid={`${testid}-download`}
      onClick={downloadPdf}
      disabled={busy}
      variant="outline"
      className="border-[#CBD2D9] text-[#0D1520] hover:bg-[#F3EFEA]"
    >
      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
      {busy ? 'Building…' : 'Masjid poster (PDF)'}
    </Button>
  );
};

export default MasjidPosterButton;
